import { Badge } from "@d12labs/ui/badge";

/** Per-meeting Fireflies analytics block as carried on `meeting.metadata`. */
type MeetingAnalytics =
	| {
			sentiments?: {
				positivePct: number | null;
				neutralPct: number | null;
				negativePct: number | null;
			} | null;
			categories?: {
				questions: number | null;
				tasks: number | null;
				metrics: number | null;
				dateTimes: number | null;
			} | null;
	  }
	| null
	| undefined;

// Category label map — `dateTimes` reads as "Dates" for the badge.
const CATEGORY_LABELS: { key: "questions" | "tasks" | "metrics" | "dateTimes"; label: string }[] = [
	{ key: "questions", label: "Questions" },
	{ key: "tasks", label: "Tasks" },
	{ key: "metrics", label: "Metrics" },
	{ key: "dateTimes", label: "Dates" },
];

/**
 * Meeting-level Fireflies analytics strip: deterministic category counts as
 * subtle badges plus an informational sentiment split. Sentiment is deliberately
 * rendered as a muted caption (NOT a /100-style score badge) — it is not a KPI.
 *
 * Renders independently of analyze state so it shows on unanalyzed meetings.
 * Returns null when the block is absent or has nothing renderable, so the page
 * can drop it in unconditionally and let it hide gracefully.
 */
export function MeetingAnalyticsStrip({ analytics }: { analytics: MeetingAnalytics }) {
	if (!analytics) return null;

	const categories = analytics.categories;
	const categoryChips = categories
		? CATEGORY_LABELS.filter(({ key }) => categories[key] != null).map(({ key, label }) => ({
				label,
				count: categories[key] as number,
			}))
		: [];

	const s = analytics.sentiments;
	const hasSentiment =
		!!s && (s.positivePct != null || s.neutralPct != null || s.negativePct != null);

	if (categoryChips.length === 0 && !hasSentiment) return null;

	return (
		<div className="flex flex-wrap items-center gap-x-3 gap-y-1.5 border-b border-hairline bg-canvas px-8 py-2.5">
			{categoryChips.length > 0 ? (
				<div className="flex flex-wrap gap-1.5">
					{categoryChips.map((chip) => (
						<Badge key={chip.label} tone="subtle">
							{chip.count} {chip.label}
						</Badge>
					))}
				</div>
			) : null}
			{hasSentiment && s ? (
				<span className="text-meta text-ink-subtle tabular-nums">
					Sentiment (informational): {s.positivePct ?? 0}% pos / {s.neutralPct ?? 0}% neu /{" "}
					{s.negativePct ?? 0}% neg
				</span>
			) : null}
		</div>
	);
}
